/**
 * C2: client-side mirror of the Identity Platform password policy.
 *
 * The server policy (scripts/configure_identity_platform.py) is the real
 * enforcement. These rules match it so a user sees the failing rule before
 * submit instead of the Firebase error after it.
 */

export const PASSWORD_MIN_LENGTH = 12;

const STRONG_LENGTH = 16;

/** Accept a regex or a predicate and return a predicate. */
const toPredicate = (test) => (typeof test === 'function' ? test : (pw) => test.test(pw));

export const PASSWORD_RULES = [
    { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (pw) => pw.length >= PASSWORD_MIN_LENGTH },
    { id: 'upper', label: 'An uppercase letter', test: /[A-Z]/ },
    { id: 'lower', label: 'A lowercase letter', test: /[a-z]/ },
    { id: 'digit', label: 'A number', test: /[0-9]/ },
].map(({ id, label, test }) => ({ id, label, passes: toPredicate(test) }));

export const PASSWORD_POLICY_MESSAGE =
    'Password must be at least 12 characters and include an uppercase letter, a lowercase letter, and a number.';

/** @returns {{ ok: boolean, failures: string[] }} ids of the rules that fail */
export function checkPassword(pw) {
    const value = pw || '';
    const failures = PASSWORD_RULES.filter((rule) => !rule.passes(value)).map((rule) => rule.id);
    return { ok: failures.length === 0, failures };
}

/**
 * Strength on a 0..4 scale for the meter. Deterministic and simple:
 *   - 0 for an empty password
 *   - one point per character class present (upper, lower, digit), 0..3
 *   - minus 1 when shorter than PASSWORD_MIN_LENGTH, floored at 0
 *   - plus 1 when STRONG_LENGTH or longer and every rule passes
 * So a 12-character password that just meets the policy scores 3, and only
 * a 16-character password with all three classes reaches 4.
 */
export function passwordStrength(pw) {
    const value = pw || '';
    if (value.length === 0) return 0;
    const classes = PASSWORD_RULES.filter((rule) => rule.id !== 'length' && rule.passes(value)).length;
    let score = classes;
    if (value.length < PASSWORD_MIN_LENGTH) score -= 1;
    if (value.length >= STRONG_LENGTH && checkPassword(value).ok) score += 1;
    return Math.max(0, Math.min(4, score));
}

const POLICY_ERROR_CODES = new Set(['auth/weak-password', 'auth/password-does-not-meet-requirements']);

/** The policy sentence for a Firebase password-policy error, else null. */
export function passwordErrorMessage(err) {
    return err && POLICY_ERROR_CODES.has(err.code) ? PASSWORD_POLICY_MESSAGE : null;
}
